// EmployeeTypeDialog.js
// Dialog for creating/editing employee types

import React, { useState, useEffect, useRef } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import MobileDialog from './MobileDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createEmployeeType, updateEmployeeType } from '../../services/employeeService';

const EmployeeTypeDialog = ({ open, onClose, employeeType }) => {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const errorRef = useRef(null);

  const isEdit = Boolean(employeeType);

  // Initialize form when dialog opens
  useEffect(() => {
    if (open) {
      if (employeeType) {
        setName(employeeType.Name || '');
        setDescription(employeeType.Description || '');
      } else {
        setName('');
        setDescription('');
      }
      setError('');
      setIsSubmitting(false);
    }
  }, [open, employeeType]);

  // Scroll to error
  useEffect(() => {
    if (error && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  const createMutation = useMutation({
    mutationFn: createEmployeeType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-types'] });
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה ביצירת סוג עובד');
      setIsSubmitting(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateEmployeeType,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-types'] });
      onClose();
    },
    onError: (err) => {
      setError(err?.response?.data?.error || 'שגיאה בעדכון סוג עובד');
      setIsSubmitting(false);
    },
  });

  const handleSubmit = async () => {
    setError('');

    if (!name.trim()) {
      setError('שם סוג העובד נדרש');
      return;
    }

    setIsSubmitting(true);

    const data = {
      Name: name.trim(),
      Description: description.trim(),
    };

    if (isEdit) {
      data.TypeID = employeeType.TypeID;
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  return (
    <MobileDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? 'עריכת סוג עובד' : 'סוג עובד חדש'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert ref={errorRef} severity="error">{error}</Alert>
          )}

          <TextField
            label="שם סוג העובד"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label="תיאור"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isSubmitting}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : (isEdit ? 'עדכון' : 'צור')}
        </Button>
      </DialogActions>
    </MobileDialog>
  );
};

export default EmployeeTypeDialog;
